import { ROLES } from "@/lib/constants/roles";

export interface TimeSlotPricingData {
  timeSlotId: string;
  pricePointId: number;
  dayOfWeek: number;
  user_role_id: number;
  id?: number;
  created_at?: string;
  updated_at?: string;
}

export interface TimeSlotPricingResponse {
  slotPricing: TimeSlotPricingData[];
  showId: number;
  userRoleId: number;
  count: number;
  originalCount?: number;
  deduplicated?: boolean;
}

/**
 * Fetch time slot pricing for a specific show and user role
 */
export async function fetchTimeSlotPricingByRole(
  venueId: number,
  showId: number,
  userRoleId: number,
): Promise<TimeSlotPricingResponse> {
  // Add a small delay for guest role to handle data propagation timing
  if (userRoleId === ROLES.GUEST_USER) {
    await new Promise((resolve) => setTimeout(resolve, 300));
  }

  const response = await fetch(
    `/api/venues/${venueId}/shows/${showId}/time-slot-pricing/by-role?user_role_id=${userRoleId}`,
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        `Failed to fetch time slot pricing: ${response.status} ${response.statusText}`,
    );
  }

  return response.json();
}

/**
 * Fetch time slot pricing for both guest and staff roles
 */
export async function fetchTimeSlotPricingForAllRoles(
  venueId: number,
  showId: number,
): Promise<{
  guestSlots: TimeSlotPricingData[];
  staffSlots: TimeSlotPricingData[];
  totalCount: number;
}> {
  try {
    const [guestResponse, staffResponse] = await Promise.all([
      fetchTimeSlotPricingByRole(venueId, showId, ROLES.GUEST_USER),
      fetchTimeSlotPricingByRole(venueId, showId, ROLES.BOOKING_STAFF),
    ]);

    return {
      guestSlots: guestResponse.slotPricing,
      staffSlots: staffResponse.slotPricing,
      totalCount: guestResponse.count + staffResponse.count,
    };
  } catch (error) {
    console.error("Error fetching time slot pricing for all roles:", error);
    throw error;
  }
}

/**
 * Check if guest slots exist for a show
 */
export async function checkGuestSlotsExist(
  venueId: number,
  showId: number,
): Promise<boolean> {
  try {
    const response = await fetchTimeSlotPricingByRole(
      venueId,
      showId,
      ROLES.GUEST_USER,
    );
    return response.count > 0;
  } catch (error) {
    console.error("Error checking guest slots:", error);
    return false;
  }
}
